import {
	ContextMenu,
	ContextMenuDeleteItem,
	IDataListColumn,
	IconButton,
	Id,
	PaginatedAutoDataList,
	Trigger,
	isSuccess,
	laravelPaginatedListFetchConfig,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { TenantIndexResource } from "../../../shared/types/tenant";
import { tenantService } from "../shared/api/tenantService";
import { createUserTenantsService } from "../shared/api/userService";
import { UserTenantsAttachDrawer } from "./UserTenantsAttachDrawer";

export const UserTenantsList: FC = () => {
	const { userId = "" } = useParams();
	const { t } = useTranslation();
	const service = useMemo(() => createUserTenantsService(userId), [userId]);
	const { showToast } = useToasts();
	const queryClient = useQueryClient();

	const onDelete = useCallback(
		async (id: Id) => {
			const res = await service.detachTenants([id]);
			if (isSuccess(res)) {
				showToast({
					type: "success",
					title: t("User Tenants"),
					body: t("Tenant was successfully removed from the user."),
				});
				await queryClient.invalidateQueries({ queryKey: service.listKey });
				await queryClient.invalidateQueries({ queryKey: service.tenantOptionsByUserKey });
				await queryClient.resetQueries({ queryKey: tenantService.indexKey });
			}
		},
		[queryClient, service, showToast, t],
	);

	const columns: IDataListColumn<TenantIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				size: "max-content",
				type: "actions",
				action: ({ id }) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuDeleteItem
							label={t("Remove tenant")}
							title={t("Remove tenant")}
							heading={t("Close")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
							action={() => onDelete(id)}
						>
							{t("Are you sure you want to remove this tenant from the user?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[onDelete, t],
	);

	return (
		<PaginatedAutoDataList
			name="userTenants"
			heading={t("Tenants")}
			empty={t("No tenant attached")}
			columns={columns}
			listFetchConfig={laravelPaginatedListFetchConfig(service.listKey, service.index)}
			headerActions={
				<Trigger render={(props) => <UserTenantsAttachDrawer {...props} userId={userId} />}>
					{(props) => <IconButton {...props} icon="plus" title={t("Add")} color="primary" size="small" />}
				</Trigger>
			}
		/>
	);
};
